<script setup lang="ts">
import type { ISportAngle } from "~/videos/types/event-angles";
import { PLAY_BASED_GAMES, SHARED_DOWNLOADABLE_FILES_ANGLE_VIDEO_TYPE_MAP, SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE } from "~/constants";
import { type IEvent, type TSportType, type TVideoSourceType, VIDEO_SOURCE_TYPE } from "~/types";
import { exportShareDownloadableFiles } from "~/videos/api/events";
import { type TEventShareDownloadableFilesSchema, useEventShareDownloadableFilesSchema } from "~/videos/schemas/event-share-downloadable-files";

const SHARE_DOWNLOADABLE_ACTION_TYPE = {
  SHARE: "share",
  DOWNLOAD_FULL: "download-full",
  DOWNLOAD_CLIPPED: "download-clipped",
} as const;

type TShareDownloadableActionType = (typeof SHARE_DOWNLOADABLE_ACTION_TYPE)[keyof typeof SHARE_DOWNLOADABLE_ACTION_TYPE];

interface IProps {
  modelValue?: boolean;
  event: IEvent;
  type: TShareDownloadableActionType;
  availableClipAngles?: ISportAngle[];
  clipAnglesLoading?: boolean;
}

const { t } = useI18n();
const toast = useToast();
const runtimeConfig = useRuntimeConfig();
const route = useRoute();

const props = withDefaults(defineProps<IProps>(), {
  type: "share",
});

const emit = defineEmits<(event: "update:modelValue") => void>();

const isOpen = useVModel(props, "modelValue", emit);

const { schema } = useEventShareDownloadableFilesSchema();

const form = reactive<TEventShareDownloadableFilesSchema>({
  type: undefined,
  emails: [],
  angles: [],
});

const emailsInput = ref("");

const { state: defaultSportTypeAngles, isLoading: isLoadingSportTypeAngles } = useAsyncState(
  getCachedAnglesBySportType(props.event.sport_type as TSportType, { accessToken: route.query.accessToken as string }),
  [],
  {
    immediate: true,
  }
);

const isPlayBasedEvent = computed(() => PLAY_BASED_GAMES.includes(props.event.sport_type));
const selectedVideoType = computed(() => SHARED_DOWNLOADABLE_FILES_ANGLE_VIDEO_TYPE_MAP[form.type ?? SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.FULL]);
const availableEventAngles = computed(() =>
  Array.from(props.event?.angles?.[selectedVideoType.value]?.keys() ?? [])?.map((angle) => ({ name: angle, label: capitalize(angle) }))
);
const anglesBySportType = computed(() => defaultSportTypeAngles.value?.filter((angle) => availableEventAngles.value?.some((a) => a.name === angle.name)) ?? []);

const isClippedVideoTypeSelected = computed(() => selectedVideoType.value === SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.CLIPPED);

const angleOptions = computed(() => {
  if (isClippedVideoTypeSelected.value) return props.availableClipAngles ?? [];
  return anglesBySportType.value?.length ? anglesBySportType.value : availableEventAngles.value;
});

const formattedAngleOptions = computed(() =>
  angleOptions.value?.length
    ? angleOptions.value
        ?.filter((angle) => angle.name !== "pano")
        ?.map((angle) => ({ value: angle.name, label: angle.label, id: "vsId" in angle ? angle.vsId : undefined }))
    : []
);

const hasMultipleAngles = computed(() => Boolean(formattedAngleOptions.value.length > 1));
const selectedTypeLabel = computed(() =>
  form.type === SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.CLIPPED || props.type === SHARE_DOWNLOADABLE_ACTION_TYPE.DOWNLOAD_CLIPPED
    ? t("labels.clipped")
    : t("labels.full")
);

function isAvailableType(type: TVideoSourceType) {
  return Boolean((props.event?.angles?.[type]?.size ?? 0) > 0);
}

const availableTypes = computed(() => {
  return {
    [SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.CLIPPED]: isAvailableType(VIDEO_SOURCE_TYPE.CLIPPED) || Boolean(props.availableClipAngles?.length),
    [SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.FULL]: isAvailableType(VIDEO_SOURCE_TYPE.CONTINUOUS),
  };
});

const isClippedType = computed(
  () =>
    isPlayBasedEvent.value &&
    (props.type === SHARE_DOWNLOADABLE_ACTION_TYPE.DOWNLOAD_CLIPPED ||
      (props.type === SHARE_DOWNLOADABLE_ACTION_TYPE.SHARE && availableTypes.value[SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.CLIPPED]))
);

// Watch for modal opening and initialize form accordingly
watch(
  [isOpen, () => props.availableClipAngles],
  ([isOpen]) => {
    if (isOpen) {
      form.type = isClippedType.value ? SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.CLIPPED : SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.FULL;
    }
  },
  { immediate: true }
);

const title = computed(() => {
  if (props.type === SHARE_DOWNLOADABLE_ACTION_TYPE.SHARE) return t("labels.share_downloadable_files");
  if (props.type === SHARE_DOWNLOADABLE_ACTION_TYPE.DOWNLOAD_FULL) return t("labels.download_full_video");
  if (props.type === SHARE_DOWNLOADABLE_ACTION_TYPE.DOWNLOAD_CLIPPED) return t("labels.download_clipped_video");
  return "";
});

const { isLoading: isSubmitting, execute } = useAsyncState(
  async () => {
    if (!form.type) {
      throw new Error(t("errors.no_selected_share_type"));
    }

    if (hasMultipleAngles.value && !form.angles.length) {
      throw new Error(t("errors.no_selected_angle"));
    }

    const selectedAngles = hasMultipleAngles.value ? formattedAngleOptions.value.filter((a) => form.angles.includes(a.value)) : formattedAngleOptions.value;

    const payload = {
      type: form.type,
      angles: selectedAngles.map(({ value, id }) => ({ name: value, id })),
      emails: form.emails,
    };

    return exportShareDownloadableFiles(props.event.id, payload, { accessToken: route.query.accessToken as string });
  },
  undefined,
  {
    immediate: false,
    onSuccess: () => {
      toast.success(
        t("toast.events.success_export_downloadable_files_description", { email: runtimeConfig.public.noReplyEmail }),
        t("toast.events.success_export_downloadable_files_title")
      );
      closeModal();
    },
    onError: (error) => {
      toast.error(parseErrorMessage(error, { t }));
    },
  }
);

function onSubmit() {
  execute();
}

function closeModal() {
  isOpen.value = false;
  clearForm();
}

function handleEmailsInput(value: string) {
  emailsInput.value = value;
  updateEmailsArray();
}

function updateEmailsArray() {
  if (!emailsInput.value.trim()) {
    form.emails = [];
    return;
  }

  form.emails = emailsInput.value
    .split(/[,;]/)
    .map((s) => s.trim())
    .filter((s) => s.length);
}

function clearForm() {
  Object.assign(form, {
    type: undefined,
    angles: [],
    emails: [],
  });
  emailsInput.value = "";
}
</script>

<template>
  <UModal :model-value="isOpen" @update:model-value="closeModal">
    <div class="p-4">
      <div class="mb-1 text-end">
        <UButton :title="t('labels.close')" variant="link-secondary" icon="close" class="size-5" @click="closeModal" />
      </div>

      <UForm class="flex flex-col gap-6 md:px-6 md:pb-6" :state="form" :schema="schema" @submit="onSubmit">
        <h2 class="text-2xl font-bold dark:text-white">
          {{ title }}
        </h2>

        <div v-if="isLoadingSportTypeAngles || (isClippedVideoTypeSelected && props.clipAnglesLoading)" class="relative h-40">
          <RLoadingOverlay class="bg-white !bg-opacity-100 dark:bg-neutral-dark-700" />
        </div>

        <div v-else class="flex w-full flex-col overflow-auto">
          <div class="flex max-h-[26rem] flex-col gap-6">
            <UFormGroup
              v-if="isPlayBasedEvent && props.type === SHARE_DOWNLOADABLE_ACTION_TYPE.SHARE && Object.values(availableTypes).every(Boolean)"
              :label="t('labels.video_type')" name="type" container-class="mt-4 flex gap-8">
              <URadio v-model="form.type" :value="SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.CLIPPED"
                :label="t('labels.clipped')" :checked="form.type === SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.CLIPPED" />
              <URadio v-model="form.type" :value="SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.FULL" :label="t('labels.full')"
                :checked="form.type === SHARED_DOWNLOADABLE_FILES_SOURCE_TYPE.FULL" />
            </UFormGroup>

            <div v-if="hasMultipleAngles" class="flex flex-col gap-2">
              <UFormGroup
                :label="t('labels.select_one_or_more_angles', { type: (type === SHARE_DOWNLOADABLE_ACTION_TYPE.SHARE ? t('labels.share') : t('labels.download')).toLowerCase() })"
                name="angles">
                <USelectMenu v-model="form.angles" :options="formattedAngleOptions"
                  :placeholder="t('errors.no_selected_angle')" nullable multiple
                  :select-all-as-item-list="formattedAngleOptions.length > 1" show-selected-options-labels />
              </UFormGroup>

              <span class="text-xs dark:text-white">{{ t("labels.share_downloadable_files_angle_tip", {
                type:
                  capitalize(selectedTypeLabel)
              }) }}</span>
            </div>

            <div class="flex flex-col gap-2">
              <UFormGroup required name="emails" :label="t('labels.email_addresses')" size="lg">
                <UTextarea v-model="emailsInput" @update:model-value="handleEmailsInput"
                  :placeholder="t('labels.enter_one_or_more_emails')" :rows="1" autoresize
                  class="max-h-[12.5rem] resize-none" />
                <template #help>{{ t('modals.events.tooltips.multiple_emails_separation') }}</template>
              </UFormGroup>
            </div>
          </div>
        </div>

        <div class="grid w-full grid-cols-2 gap-4">
          <UButton size="lg" variant="secondary" class="px-3" @click="closeModal">
            {{ t("labels.cancel") }}
          </UButton>
          <UButton size="lg" variant="primary" type="submit" :disabled="isSubmitting">
            {{ type === SHARE_DOWNLOADABLE_ACTION_TYPE.SHARE ? t("labels.share") : t("labels.send") }}
          </UButton>
        </div>
      </UForm>
    </div>
  </UModal>
</template>
